import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import * as LucideIcons from 'lucide-react';

const PitchDeck = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    // Slide 1: Cover
    {
      id: 1,
      type: 'cover',
      title: 'YardEdge',
      subtitle: 'Distributed AI Infrastructure',
      tagline: 'Transforming backyards into the backbone of tomorrow computing',
      background: 'bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900'
    },

    // Slide 2: Problem
    {
      id: 2,
      type: 'content',
      title: 'The Problem',
      subtitle: 'Centralized data centers cannot keep up with AI demand',
      points: [
        {
          icon: 'TrendingUp',
          text: 'AI computing demand growing 50% annually',
          stat: '2.5 exabytes/day by 2025'
        },
        {
          icon: 'Zap',
          text: 'Data centers consume 3% of global electricity',
          stat: '460 TWh/year'
        },
        {
          icon: 'AlertCircle',
          text: 'Latency kills real-time AI applications',
          stat: '>100ms to cloud'
        },
        {
          icon: 'DollarSign',
          text: 'Construction costs skyrocketing',
          stat: '$1B+ per facility'
        }
      ]
    },

    // Slide 3: Solution
    {
      id: 3,
      type: 'solution',
      title: 'The Solution',
      subtitle: 'Solar-powered mini data centers in residential backyards',
      features: [
        {
          icon: 'Home',
          title: 'Distributed',
          desc: 'Deploy where compute is needed'
        },
        {
          icon: 'Sun',
          title: '100% Renewable',
          desc: 'Solar + battery powered'
        },
        {
          icon: 'Gauge',
          title: 'Low Latency',
          desc: '<5ms to users'
        },
        {
          icon: 'Users',
          title: 'Community Owned',
          desc: 'Homeowners earn passive income'
        }
      ]
    },

    // Slide 4: Market Opportunity
    {
      id: 4,
      type: 'market',
      title: 'Market Opportunity',
      subtitle: 'Three massive trends converging',
      markets: [
        {
          name: 'Edge Computing',
          size: '$155B',
          year: '2030',
          cagr: '37.4%',
          color: 'from-blue-500 to-cyan-500'
        },
        {
          name: 'Residential Solar',
          size: '$76B',
          year: '2032',
          cagr: '13.9%',
          color: 'from-yellow-500 to-orange-500'
        },
        {
          name: 'AI Infrastructure',
          size: '$400B',
          year: '2027',
          cagr: '28.5%',
          color: 'from-purple-500 to-pink-500'
        }
      ]
    },

    // Slide 5: Product
    {
      id: 5,
      type: 'product',
      title: 'The YardEdge Unit',
      subtitle: 'Enterprise-grade computing, backyard-scale',
      specs: [
        { label: 'Size', value: '8x6x7 feet' },
        { label: 'Power', value: '15-20kW solar' },
        { label: 'Storage', value: '40kWh battery' },
        { label: 'Compute', value: '384 cores, 3TB RAM' },
        { label: 'Network', value: '10Gbps fiber' },
        { label: 'Noise', value: '<45dB (whisper quiet)' }
      ]
    },

    // Slide 6: Business Model
    {
      id: 6,
      type: 'business',
      title: 'Business Model',
      subtitle: 'Three revenue streams',
      revenues: [
        {
          icon: 'ShoppingCart',
          title: 'Hardware Sales',
          desc: '$15K - $45K per unit',
          margin: '30% gross margin',
          color: 'bg-blue-500'
        },
        {
          icon: 'Server',
          title: 'Compute Revenue Share',
          desc: '40% of compute fees',
          margin: '$300-$1,800/mo per unit',
          color: 'bg-green-500'
        },
        {
          icon: 'Leaf',
          title: 'Energy Credits',
          desc: 'Carbon offsets & RECs',
          margin: '$50-$200/mo per unit',
          color: 'bg-emerald-500'
        }
      ]
    },

    // Slide 7: Traction
    {
      id: 7,
      type: 'traction',
      title: 'Traction & Milestones',
      achievements: [
        {
          icon: 'CheckCircle2',
          title: 'Website Launched',
          desc: 'Professional landing page live',
          status: 'complete'
        },
        {
          icon: 'CheckCircle2',
          title: 'Technical Validation',
          desc: 'Hardware BOM & specs defined',
          status: 'complete'
        },
        {
          icon: 'Clock',
          title: 'Prototype Build',
          desc: 'Q1 2026 - First unit assembly',
          status: 'upcoming'
        },
        {
          icon: 'Clock',
          title: 'Pilot Program',
          desc: 'Q2 2026 - 10 homeowner installs',
          status: 'upcoming'
        },
        {
          icon: 'Clock',
          title: 'Production Scale',
          desc: 'Q4 2026 - 100+ units deployed',
          status: 'upcoming'
        }
      ]
    },

    // Slide 8: Go-to-Market
    {
      id: 8,
      type: 'gtm',
      title: 'Go-to-Market Strategy',
      phases: [
        {
          phase: 'Phase 1',
          title: 'Evangelists',
          target: 'Tech-savvy homeowners',
          channels: ['Reddit', 'HN', 'Twitter'],
          size: '100 units'
        },
        {
          phase: 'Phase 2',
          title: 'Early Adopters',
          target: 'Solar + crypto communities',
          channels: ['Partnerships', 'Direct sales'],
          size: '1,000 units'
        },
        {
          phase: 'Phase 3',
          title: 'Early Majority',
          target: 'Mainstream homeowners',
          channels: ['Solar installers', 'Utilities'],
          size: '10,000+ units'
        }
      ]
    },

    // Slide 9: Financials
    {
      id: 9,
      type: 'financials',
      title: '5-Year Financial Projections',
      projections: [
        { year: 'Year 1', units: 50, revenue: '$0.9M', costs: '$1.2M', profit: '-$0.3M' },
        { year: 'Year 2', units: 250, revenue: '$5.8M', costs: '$4.2M', profit: '$1.6M' },
        { year: 'Year 3', units: 1000, revenue: '$28M', costs: '$18M', profit: '$10M' },
        { year: 'Year 4', units: 3000, revenue: '$95M', costs: '$52M', profit: '$43M' },
        { year: 'Year 5', units: 7500, revenue: '$265M', costs: '$125M', profit: '$140M' }
      ]
    },

    // Slide 10: The Ask
    {
      id: 10,
      type: 'ask',
      title: 'The Ask',
      subtitle: 'Pre-Seed Round',
      amount: '$750K',
      equity: '15-20%',
      uses: [
        { category: 'Prototype Build', amount: '$150K', percent: 20 },
        { category: 'Pilot Program (10 units)', amount: '$200K', percent: 27 },
        { category: 'Team (2-3 hires)', amount: '$300K', percent: 40 },
        { category: 'Operations & Legal', amount: '$100K', percent: 13 }
      ],
      milestones: [
        'Working prototype installed',
        '10 pilot units deployed',
        'Proven unit economics',
        'Ready for Seed round ($2M-$3M)'
      ]
    }
  ];

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const renderSlide = (slide) => {
    switch (slide.type) {
      case 'cover':
        return (
          <div className={`h-full flex flex-col items-center justify-center ${slide.background} text-white p-12`}>
            <h1 className="text-8xl font-bold mb-6">{slide.title}</h1>
            <h2 className="text-4xl font-light mb-4">{slide.subtitle}</h2>
            <p className="text-2xl text-gray-300 italic">{slide.tagline}</p>
          </div>
        );

      case 'content':
        return (
          <div className="h-full p-12 bg-white">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">{slide.title}</h2>
            <p className="text-2xl text-gray-600 mb-12">{slide.subtitle}</p>
            <div className="grid grid-cols-2 gap-8">
              {slide.points.map((point, idx) => {
                const Icon = LucideIcons[point.icon];
                return (
                  <Card key={idx} className="bg-gray-50 border-2 border-gray-200 hover:border-orange-500 transition-colors">
                    <CardContent className="p-6">
                      <Icon className="w-12 h-12 text-orange-500 mb-4" />
                      <p className="text-xl text-gray-900 mb-2">{point.text}</p>
                      <p className="text-3xl font-bold text-orange-600">{point.stat}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );

      case 'solution':
        return (
          <div className="h-full p-12 bg-gradient-to-br from-blue-50 to-cyan-50">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">{slide.title}</h2>
            <p className="text-2xl text-gray-600 mb-12">{slide.subtitle}</p>
            <div className="grid grid-cols-4 gap-6">
              {slide.features.map((feature, idx) => {
                const Icon = LucideIcons[feature.icon];
                return (
                  <div key={idx} className="text-center">
                    <div className="w-24 h-24 mx-auto bg-white rounded-full flex items-center justify-center mb-4 shadow-lg">
                      <Icon className="w-12 h-12 text-blue-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-lg text-gray-600">{feature.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'market':
        return (
          <div className="h-full p-12 bg-white">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">{slide.title}</h2>
            <p className="text-2xl text-gray-600 mb-12">{slide.subtitle}</p>
            <div className="grid grid-cols-3 gap-8">
              {slide.markets.map((market, idx) => (
                <div key={idx} className="text-center">
                  <div className={`bg-gradient-to-br ${market.color} text-white p-8 rounded-xl mb-4`}>
                    <h3 className="text-2xl font-bold mb-2">{market.name}</h3>
                    <p className="text-6xl font-bold mb-2">{market.size}</p>
                    <p className="text-xl">by {market.year}</p>
                  </div>
                  <p className="text-2xl font-semibold text-gray-600">{market.cagr} CAGR</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'product':
        return (
          <div className="h-full p-12 bg-gradient-to-br from-slate-50 to-gray-100">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">{slide.title}</h2>
            <p className="text-2xl text-gray-600 mb-12">{slide.subtitle}</p>
            <div className="grid grid-cols-3 gap-6">
              {slide.specs.map((spec, idx) => (
                <div key={idx} className="bg-white p-6 rounded-lg shadow-md">
                  <p className="text-lg text-gray-500 mb-2">{spec.label}</p>
                  <p className="text-3xl font-bold text-gray-900">{spec.value}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'business':
        return (
          <div className="h-full p-12 bg-white">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">{slide.title}</h2>
            <p className="text-2xl text-gray-600 mb-12">{slide.subtitle}</p>
            <div className="grid grid-cols-3 gap-8">
              {slide.revenues.map((rev, idx) => {
                const Icon = LucideIcons[rev.icon];
                return (
                  <div key={idx} className="text-center">
                    <div className={`${rev.color} text-white p-8 rounded-xl mb-4`}>
                      <Icon className="w-16 h-16 mx-auto mb-4" />
                      <h3 className="text-2xl font-bold mb-2">{rev.title}</h3>
                      <p className="text-xl mb-2">{rev.desc}</p>
                      <p className="text-lg font-semibold">{rev.margin}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'traction':
        return (
          <div className="h-full p-12 bg-gradient-to-br from-green-50 to-emerald-50">
            <h2 className="text-5xl font-bold text-gray-900 mb-12">{slide.title}</h2>
            <div className="space-y-6">
              {slide.achievements.map((achievement, idx) => {
                const Icon = LucideIcons[achievement.icon];
                return (
                  <div key={idx} className="flex items-center gap-6 bg-white p-6 rounded-lg shadow-md">
                    <Icon className={`w-12 h-12 ${achievement.status === 'complete' ? 'text-green-600' : 'text-gray-400'}`} />
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">{achievement.title}</h3>
                      <p className="text-xl text-gray-600">{achievement.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'gtm':
        return (
          <div className="h-full p-12 bg-white">
            <h2 className="text-5xl font-bold text-gray-900 mb-12">{slide.title}</h2>
            <div className="grid grid-cols-3 gap-8">
              {slide.phases.map((phase, idx) => (
                <Card key={idx} className="bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-200">
                  <CardContent className="p-6">
                    <p className="text-sm font-bold text-orange-600 mb-2">{phase.phase}</p>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">{phase.title}</h3>
                    <p className="text-lg text-gray-700 mb-4">{phase.target}</p>
                    <div className="mb-4">
                      {phase.channels.map((channel, cidx) => (
                        <span key={cidx} className="inline-block bg-white px-3 py-1 rounded-full text-sm mr-2 mb-2">{channel}</span>
                      ))}
                    </div>
                    <p className="text-2xl font-bold text-orange-600">{phase.size}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );

      case 'financials':
        return (
          <div className="h-full p-12 bg-white">
            <h2 className="text-5xl font-bold text-gray-900 mb-12">{slide.title}</h2>
            <table className="w-full text-left">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="text-2xl pb-4">Year</th>
                  <th className="text-2xl pb-4">Units</th>
                  <th className="text-2xl pb-4">Revenue</th>
                  <th className="text-2xl pb-4">Costs</th>
                  <th className="text-2xl pb-4">Profit</th>
                </tr>
              </thead>
              <tbody>
                {slide.projections.map((proj, idx) => (
                  <tr key={idx} className="border-b border-gray-200">
                    <td className="text-xl py-4 font-semibold">{proj.year}</td>
                    <td className="text-xl py-4">{proj.units}</td>
                    <td className="text-xl py-4 text-green-600 font-bold">{proj.revenue}</td>
                    <td className="text-xl py-4 text-red-600">{proj.costs}</td>
                    <td className="text-xl py-4 font-bold">{proj.profit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case 'ask':
        return (
          <div className="h-full p-12 bg-gradient-to-br from-orange-50 to-red-50">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">{slide.title}</h2>
            <p className="text-3xl text-gray-600 mb-2">{slide.subtitle}</p>
            <p className="text-7xl font-bold text-orange-600 mb-8">{slide.amount}</p>
            <p className="text-2xl text-gray-600 mb-12">for {slide.equity} equity</p>
            
            <div className="grid grid-cols-2 gap-8">
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">Use of Funds</h3>
                {slide.uses.map((use, idx) => (
                  <div key={idx} className="mb-4">
                    <div className="flex justify-between mb-2">
                      <span className="text-xl">{use.category}</span>
                      <span className="text-xl font-bold">{use.amount}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-orange-600 h-3 rounded-full" style={{width: `${use.percent}%`}}></div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">Key Milestones</h3>
                <ul className="space-y-4">
                  {slide.milestones.map((milestone, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <LucideIcons.CheckCircle2 className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
                      <span className="text-xl">{milestone}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );

      default:
        return <div className="h-full p-12 bg-white">Slide type not implemented</div>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Slide Container */}
      <div className="flex-1 relative">
        <div className="h-full">
          {renderSlide(slides[currentSlide])}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          disabled={currentSlide === 0}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white p-4 rounded-full shadow-lg disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <LucideIcons.ChevronLeft className="w-8 h-8 text-gray-900" />
        </button>

        <button
          onClick={nextSlide}
          disabled={currentSlide === slides.length - 1}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white p-4 rounded-full shadow-lg disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <LucideIcons.ChevronRight className="w-8 h-8 text-gray-900" />
        </button>
      </div>

      {/* Bottom Control Bar */}
      <div className="bg-gray-800 p-4">
        <div className="container mx-auto flex items-center justify-between">
          {/* Slide Indicators */}
          <div className="flex gap-2">
            {slides.map((_, idx) => (
              <button
                key={idx}
                onClick={() => goToSlide(idx)}
                className={`w-3 h-3 rounded-full transition-all ${ 
                  idx === currentSlide 
                    ? 'bg-orange-500 w-8' 
                    : 'bg-gray-600 hover:bg-gray-500'
                }`}
              />
            ))}
          </div>

          {/* Slide Counter */}
          <div className="text-white text-lg">
            {currentSlide + 1} / {slides.length}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <Button 
              variant="outline" 
              className="bg-white text-gray-900 hover:bg-gray-100"
              onClick={() => window.print()}
            >
              <LucideIcons.Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            <Button 
              className="bg-orange-600 hover:bg-orange-700 text-white"
              onClick={() => window.location.href = '/#contact'}
            >
              <LucideIcons.Mail className="w-4 h-4 mr-2" />
              Contact Us
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PitchDeck;
